import React from 'react';
import { useParams } from 'react-router-dom';

import { IParams } from '@domain/interfaces/IParams';
import { ICustomValue } from '@domain/interfaces/dashboard/CustomValues/ICustomValue';
import { ICustomValuesOutContext } from '@domain/interfaces/dashboard/CustomValues/ICustomValuesContext';
import { ISelectedFilter } from '@domain/interfaces/components/IFilter';

import { useToast } from '@helpers/hooks/useToast';
import { useConfig } from '@helpers/hooks/useConfig';

import { parseDataFilter } from '@helpers/utils/components/filter';

import { PRE_DEFINED_CUSTOM_VALUES_CATEGORIES } from '@constants/predefinedCustomValuesCategories';

import customValueService from '@services/pages/dashboard/customValue/customValue';
import customValuesCategoriesService from '@services/pages/dashboard/customValuesCategories/customValueCategories';

const CustomValuesOutContext = React.createContext<ICustomValuesOutContext | null>(null);

export const CustomValuesOutProvider: React.FC = ({ children }) => {
  const { storeAliasId } = useParams<IParams>();
  const { toast } = useToast();
  const { analytics, user } = useConfig();

  const [filterData, setFilterData] = React.useState<Array<ISelectedFilter>>([]);
  const [page, setPage] = React.useState<number>(0);
  const [pageCount, setPageCount] = React.useState<number>(1);
  const [isNewCustomValueSidemodalOpen, setIsNewCustomValueSidemodalOpen] = React.useState<boolean>(
    false,
  );
  const [
    isEditCustomValueSidemodalOpen,
    setIsEditCustomValueSidemodalOpen,
  ] = React.useState<boolean>(false);
  const [isCreatingCustomValue, setIsCreatingCustomValue] = React.useState<boolean>(false);
  const [
    isCreatingPredefinedCustomValueCategory,
    setIsCreatingPredefinedCustomValueCategory,
  ] = React.useState<boolean>(false);
  const [isUpdatingCustomValue, setIsUpdatingCustomValue] = React.useState<boolean>(false);
  const [isDeletingCustomValue, setIsDeletingCustomValue] = React.useState<boolean>(false);
  const [customValueToEdit, setCustomValueToEdit] = React.useState<ICustomValue | undefined>(
    undefined,
  );
  const [selectedCustomValueChecked, setSelectedCustomValueChecked] = React.useState<
    ICustomValue | undefined
  >(undefined);

  const handleFilterData = React.useCallback(newData => {
    setFilterData(newData);
  }, []);

  const {
    customValues,
    isValidating: isValidatingCustomValues,
    error: customValuesError,
    isLoading: isLoadingCustomValues,
    mutate: mutateCustomValues,
    totalPages,
  } = customValueService.getCustomValues({
    storeAliasId,
    page,
    filter: {
      is_spend: 'true',
      ...parseDataFilter(filterData),
    },
  });
  const {
    customValuesCategories,
    error: customValuesCategoriesError,
    isLoading: isLoadingCustomValuesCategories,
    mutate: mutateCustomValuesCategories,
    isValidating: isValidatingCustomValuesCategories,
  } = customValuesCategoriesService.getCustomValuesCategories({ storeAliasId });

  const handleSelectedCustomValueChecked = React.useCallback(
    (customValue: ICustomValue | undefined) => setSelectedCustomValueChecked(customValue),
    [],
  );

  const handleCustomValueToEdit = React.useCallback(
    (customValue: ICustomValue | undefined) => setCustomValueToEdit(customValue),
    [],
  );

  const onPageChange = React.useCallback(({ selected }) => setPage(selected), []);

  const handleEditCustomValueSidemodalOpen = React.useCallback(
    () => setIsEditCustomValueSidemodalOpen(!isEditCustomValueSidemodalOpen),
    [isEditCustomValueSidemodalOpen],
  );

  const openNewCustomValuesSideModal = React.useCallback(
    () => setIsNewCustomValueSidemodalOpen(true),
    [],
  );

  const handleNewCustomValueSidemodalOpen = React.useCallback(
    () => setIsNewCustomValueSidemodalOpen(!isNewCustomValueSidemodalOpen),
    [isNewCustomValueSidemodalOpen],
  );

  const findCustomValueCategoryData = React.useCallback(
    (customValue: ICustomValue) => {
      let foundCategory;

      if (customValuesCategories) {
        foundCategory = customValuesCategories.find(
          category => category.id === customValue.custom_value_category_id,
        );
      }

      return foundCategory;
    },
    [customValuesCategories],
  );

  const createPredefinedCustomValuesCategories = React.useCallback(
    async (preDefinedCategoriesToBeCreated: typeof PRE_DEFINED_CUSTOM_VALUES_CATEGORIES) => {
      setIsCreatingPredefinedCustomValueCategory(true);

      try {
        if (preDefinedCategoriesToBeCreated.length) {
          const promises = preDefinedCategoriesToBeCreated.map(category => {
            return customValuesCategoriesService.createCustomValueCategory({
              storeAliasId,
              data: category,
            });
          });

          await Promise.all(promises);

          await mutateCustomValuesCategories();
        }

        setIsCreatingPredefinedCustomValueCategory(false);
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
        setIsCreatingPredefinedCustomValueCategory(false);
      }
    },
    [storeAliasId, toast, mutateCustomValuesCategories],
  );

  const verifyPreDefinedCustomValuesCategories = React.useCallback(() => {
    const preDefinedCategoriesToBeCreated = PRE_DEFINED_CUSTOM_VALUES_CATEGORIES.filter(
      preDefinedCategory =>
        !customValuesCategories.find(category =>
          Boolean(category.name === preDefinedCategory.name),
        ),
    );

    createPredefinedCustomValuesCategories(preDefinedCategoriesToBeCreated);
  }, [customValuesCategories, createPredefinedCustomValuesCategories]);

  const createCustomValue = React.useCallback(
    async data => {
      setIsCreatingCustomValue(true);

      try {
        await customValueService.createCustomValue({
          storeAliasId,
          customValuesCategoryAliasId: data.category,
          data: {
            amount: data.amount,
            currency: data.currency,
            description: data.description,
            end_date: data.end_date,
            is_active: data.is_active,
            is_spend: true,
            period: data.period,
            start_date: data.start_date,
            send_notification: data.send_notification,
          },
        });

        if (data.send_notification) {
          analytics?.track(
            'Has Selected To Notify Custom Value',
            { email: user.email },
            { context: { groupId: storeAliasId } },
          );
        }

        analytics?.track(
          'Custom Values Added',
          {
            amount: data.amount,
            currency: data.currency,
            description: data.description,
            period: data.period,
            end_date: data.end_date,
            start_date: data.start_date,
            send_notification: data.send_notification,
            is_spend: true,
            email: user?.email,
          },
          { context: { groupId: storeAliasId } },
        );
        analytics?.trackHubSpot(storeAliasId, { additional_value_added: true });

        toast.success('Valor adicional criado com sucesso!');

        setIsNewCustomValueSidemodalOpen(false);

        await mutateCustomValues();
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      } finally {
        setIsCreatingCustomValue(false);
      }
    },
    [mutateCustomValues, toast, storeAliasId, analytics, user],
  );

  const updateCustomValue = React.useCallback(
    async data => {
      setIsUpdatingCustomValue(true);

      try {
        await customValueService.updateCustomValue({
          storeAliasId,
          customValueAliasId: customValueToEdit?.alias_id,
          data,
        });

        toast.success('Valor adicional atualizado com sucesso!');

        handleEditCustomValueSidemodalOpen();

        await mutateCustomValues();
      } catch {
        toast.error(
          'Ocorreu um problema ao atualizar o valor adicional! Por favor, tente novamente.',
        );
      } finally {
        setIsUpdatingCustomValue(false);
      }
    },
    [
      customValueToEdit,
      toast,
      mutateCustomValues,
      handleEditCustomValueSidemodalOpen,
      storeAliasId,
    ],
  );

  const deleteCustomValue = React.useCallback(
    async customValueAliasId => {
      setIsDeletingCustomValue(true);

      try {
        await customValueService.deleteCustomValue({ storeAliasId, customValueAliasId });

        toast.success('Valor adicional excluído com sucesso!');

        await mutateCustomValues();
      } catch {
        toast.error(
          'Ocorreu um problema ao excluir o valor adicional! Por favor, tente novamente.',
        );
      } finally {
        setIsDeletingCustomValue(false);
      }
    },
    [mutateCustomValues, toast, storeAliasId],
  );

  React.useEffect(() => {
    if (totalPages) {
      setPageCount(totalPages);
    }
  }, [totalPages]);

  return (
    <CustomValuesOutContext.Provider
      value={{
        createCustomValue,
        customValueToEdit,
        customValues,
        customValuesCategories,
        customValuesCategoriesError,
        customValuesError,
        deleteCustomValue,
        findCustomValueCategoryData,
        handleCustomValueToEdit,
        handleEditCustomValueSidemodalOpen,
        handleNewCustomValueSidemodalOpen,
        handleSelectedCustomValueChecked,
        isCreatingCustomValue,
        isCreatingPredefinedCustomValueCategory,
        isDeletingCustomValue,
        openNewCustomValuesSideModal,
        isEditCustomValueSidemodalOpen,
        isLoadingCustomValues,
        isLoadingCustomValuesCategories,
        isNewCustomValueSidemodalOpen,
        isUpdatingCustomValue,
        isValidatingCustomValues,
        isValidatingCustomValuesCategories,
        mutateCustomValues,
        mutateCustomValuesCategories,
        onPageChange,
        page,
        pageCount,
        selectedCustomValueChecked,
        updateCustomValue,
        verifyPreDefinedCustomValuesCategories,
        filterData,
        handleFilterData,
      }}
    >
      {children}
    </CustomValuesOutContext.Provider>
  );
};

export const useCustomValuesOut = (): ICustomValuesOutContext => {
  const context = React.useContext(CustomValuesOutContext);

  if (!context) {
    throw new Error('useCustomValuesOut must be used within CustomValuesOutProvider');
  }

  return context;
};
